// Ficha pública detallada por tipo de bono / membresía.
// URL: /bonos/:slug
// Datos: GET /api/bonos-tipos/:slug

import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Ticket, Infinity as InfinityIcon, Calendar, Check, Loader2, AlertCircle,
} from 'lucide-react';
import { API_BASE, crearCheckoutBono, ApiError } from '../lib/api';
import { useAuth, readToken } from '../context/AuthContext';

function formatEuros(n) {
  if (n == null) return '';
  const num = typeof n === 'number' ? n : Number(n);
  if (Number.isInteger(num)) return `${num} €`;
  return `${num.toFixed(2).replace('.', ',')} €`;
}

export default function Bono() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [comprando, setComprando] = useState(false);
  const [errorCompra, setErrorCompra] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(null);
    fetch(`${API_BASE}/api/bonos-tipos/${encodeURIComponent(slug)}?idioma=${lang}`)
      .then((r) => r.ok ? r.json() : r.json().then((b) => Promise.reject(b)))
      .then((json) => { if (!cancelled) setData(json); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [slug, lang]);

  const onComprar = async () => {
    if (!data?.bono?.id) return;
    setErrorCompra(null);
    if (!isAuthenticated) {
      navigate(`/login?next=${encodeURIComponent(`/bonos/${slug}`)}`);
      return;
    }
    const token = readToken();
    if (!token) {
      navigate(`/login?next=${encodeURIComponent(`/bonos/${slug}`)}`);
      return;
    }
    setComprando(true);
    try {
      const r = await crearCheckoutBono(token, data.bono.id);
      if (r?.url) {
        window.location.href = r.url;
      } else {
        setErrorCompra(t('bono_detalle.error_checkout'));
        setComprando(false);
      }
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setErrorCompra(t('bono_detalle.errores.' + code, { defaultValue: t('bono_detalle.error_checkout') }));
      setComprando(false);
    }
  };

  if (error) {
    return (
      <main className="clase-page">
        <div className="clase-page-inner">
          <button type="button" className="clase-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} strokeWidth={1.5} /> {t('comun.volver')}
          </button>
          <div className="clase-page-error">
            {error?.code === 'BONO_NO_ENCONTRADO'
              ? t('bono_detalle.no_encontrado')
              : t('bonos.error_carga')}
          </div>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="clase-page">
        <div className="clase-page-inner">
          <div className="clase-page-skeleton" />
        </div>
      </main>
    );
  }

  const { bono } = data;
  const tieneDescLarga = !!(bono.descripcion_larga && bono.descripcion_larga.trim());
  const creditosTexto = bono.es_membresia
    ? t('bonos.ilimitado')
    : t('bonos.clases', { count: bono.creditos_incluidos });

  return (
    <main className="clase-page">
      <div className="clase-page-inner">
        <button type="button" className="clase-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} strokeWidth={1.5} /> {t('comun.volver')}
        </button>

        <header className="clase-page-head">
          <div className="clase-page-icon-wrap">
            {bono.es_membresia
              ? <InfinityIcon size={48} strokeWidth={1.2} className="clase-page-icon" />
              : <Ticket size={48} strokeWidth={1.2} className="clase-page-icon" />}
          </div>
          <div className="clase-page-head-info">
            <span className="bono-page-tipo">
              {bono.es_membresia ? t('bono_detalle.tipo_membresia') : t('bono_detalle.tipo_bono')}
            </span>
            <h1 className="clase-page-title">{bono.nombre}</h1>
            <div className="bono-page-precio">{formatEuros(bono.precio_euros)}</div>
          </div>
        </header>

        <p className="clase-page-resumen">{bono.descripcion}</p>

        <section className="clase-page-cuerpo">
          {tieneDescLarga ? (
            bono.descripcion_larga.split(/\n\n+/).map((parrafo, i) => (
              <p key={i} className="clase-page-parrafo">{parrafo.trim()}</p>
            ))
          ) : (
            <p className="clase-page-placeholder">
              {t('bono_detalle.placeholder_sin_contenido')}
            </p>
          )}
        </section>

        <section className="bono-page-features">
          <h2 className="clase-page-sec-title">{t('bono_detalle.que_incluye')}</h2>
          <ul className="bono-features-lista">
            <li>
              <Check size={16} strokeWidth={1.5} />
              <span>{creditosTexto}</span>
            </li>
            {bono.validez_dias != null && (
              <li>
                <Calendar size={16} strokeWidth={1.5} />
                <span>{t('bonos.validez', { dias: bono.validez_dias })}</span>
              </li>
            )}
            <li>
              <Check size={16} strokeWidth={1.5} />
              <span>{t('bono_detalle.aplica_' + bono.aplica_a)}</span>
            </li>
          </ul>
        </section>

        {errorCompra && (
          <div className="modal-error-general bono-page-error">
            <AlertCircle size={16} strokeWidth={1.75} />
            <span>{errorCompra}</span>
          </div>
        )}

        <div className="bono-page-cta-row">
          {bono.tipo !== 'clase_suelta' && bono.precio_euros > 0 && (
            <button
              type="button"
              className="clase-page-cta bono-page-cta-comprar"
              onClick={onComprar}
              disabled={comprando}
            >
              {comprando ? (
                <><Loader2 size={16} strokeWidth={1.5} className="spin" /> {t('auth.enviando')}</>
              ) : (
                t('bono_detalle.cta_comprar')
              )}
            </button>
          )}
          <Link to="/horarios" className="clase-page-cta clase-page-cta--secondary">
            {t('bono_detalle.cta_ver_horarios')}
          </Link>
        </div>
      </div>
    </main>
  );
}
